import { Cancel, Email, Language, Link, LinkedIn, Person, Save, Share } from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EEntityType, entityTypeToLink, validateUrlSlug } from '@sharedInterfaces/globalEnums';
import type { EGender } from '@sharedInterfaces/IEmployee';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import updateMyEmail from '@src/APIs/graphQl/Employee/updateMyEmail';
import updateMyFirstName from '@src/APIs/graphQl/Employee/updateMyFirstName';
import updateMyGender from '@src/APIs/graphQl/Employee/updateMyGender';
import updateMyLastName from '@src/APIs/graphQl/Employee/updateMyLastName';
import updateMyLinkedInUrl from '@src/APIs/graphQl/Employee/updateMyLinkedInUrl';
import updateMyPhoneNumber from '@src/APIs/graphQl/Employee/updateMyPhoneNumber';
import updateMySlackUrl from '@src/APIs/graphQl/Employee/updateMySlackUrl';
import updateMyWebsiteUrl from '@src/APIs/graphQl/Employee/updateMyWebsiteUrl';

import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';

import { stageContext } from '@src/globals';
import {
    setEmployeeGender,
    setEmployeeLinkedInUrl,
    setEmployeeNames,
    setEmployeePhoneNumber,
    setEmployeeSlackUrl,
    setEmployeeWebsiteUrl,
} from '@store/reducer/employeeReducer';
import { setUserEmail } from '@store/reducer/userReducer';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DescriptionEditor } from './DescriptionEditor';
import { ProfileTabHeader } from './ProfileTabHeader';

interface EditableFieldProps {
    label: string;
    value: string;
    icon: React.ReactNode;
    onSave: (value: string) => Promise<void>;
    multiline?: boolean;
    disabled?: boolean;
}

function EditableField({ label, value, icon, onSave, multiline = false, disabled = false }: EditableFieldProps) {
    const theme = useTheme();
    const [editValue, setEditValue] = useState(value);
    const [isSaving, setIsSaving] = useState(false);
    const hasChanges = editValue !== value;

    const handleSave = async () => {
        if (!hasChanges) return;

        setIsSaving(true);
        try {
            await onSave(editValue);
        } catch (error) {
            console.error('Save failed:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setEditValue(value);
    };

    // Update local state when prop value changes
    React.useEffect(() => {
        setEditValue(value);
    }, [value]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: theme.palette.primary.main }}>{icon}</Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    {label}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: 1 }}>
                <TextField
                    fullWidth
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    multiline={multiline}
                    rows={multiline ? 3 : 1}
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    placeholder={value || 'Nicht angegeben'}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: hasChanges
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.background.paper, 0.5),
                        },
                    }}
                />

                {hasChanges && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                            size="small"
                            onClick={handleSave}
                            disabled={isSaving || disabled}
                            sx={{
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) },
                            }}
                        >
                            <Save fontSize="small" sx={{ color: theme.palette.success.main }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleCancel}
                            disabled={isSaving}
                            sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) },
                            }}
                        >
                            <Cancel fontSize="small" sx={{ color: theme.palette.error.main }} />
                        </IconButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

/**
 * Represents a general tab component.
 *
 * @returns {JSX.Element} The JSX element representing the general tab.
 */
export function GeneralTab(): JSX.Element {
    const theme = useTheme();
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).profileSettings;
    const employee = useSelector((state: AppState) => state.employee);
    const user = useSelector((state: AppState) => state.user);
    const OUs = useSelector((state: AppState) => state.OUs);
    const offline = useSelector((state: AppState) => state.client.offline);
    const showErrorDialog = useErrorDialog();

    const OU = OUs?.find((o) => o.employees.includes(employee.id));

    // Dropdown options
    const PRONOUNS = [
        'er/ihm',
        'sie/ihr',
        'Divers',
        // 'they/them', // Not supported yet
        // 'er/sie', // Not supported yet
    ];

    // Real save functions from original GeneralTab
    const handleSaveFirstName = async (value: string) => {
        try {
            const result = await updateMyFirstName(value);
            if (result) {
                dispatch(setEmployeeNames({ firstname: value, lastname: employee.lastName }));
            }
        } catch (ex) {
            showErrorDialog(ex as Error);
            throw ex;
        }
    };

    const handleSaveLastName = async (value: string) => {
        try {
            const result = await updateMyLastName(value);
            if (result) {
                dispatch(setEmployeeNames({ firstname: employee.firstName, lastname: value }));
            }
        } catch (ex) {
            showErrorDialog(ex as Error);
            throw ex;
        }
    };

    const handleSaveEmail = async (value: string) => {
        try {
            const result = await updateMyEmail(value);
            if (result) {
                dispatch(setUserEmail(value));
            }
        } catch (ex) {
            showErrorDialog(ex as Error);
            throw ex;
        }
    };

    // const handleSaveLanguage = async (value: ELanguage | 'default') => {
    //     try {
    //         const realValue: ELanguage | null = value === 'default' ? null : (value as ELanguage);
    //         await updateUserLanguage(realValue);
    //         dispatch(setLanguage(realValue));
    //         if (realValue) {
    //             dispatch(setEmployeeLang(realValue));
    //         } else {
    //             dispatch(setEmployeeLang(findMatchingLanguage(window.navigator.languages)));
    //         }
    //     } catch (ex) {
    //         showErrorDialog(ex as Error);
    //         throw ex;
    //     }
    // };

    // Save functions for new fields
    const handleSavePronouns = async (value: string) => {
        try {
            // Map pronoun to EGender
            let gender: EGender;
            if (value === 'er/ihm') gender = 'male' as EGender;
            else if (value === 'sie/ihr') gender = 'female' as EGender;
            else if (value === 'Divers') gender = 'other' as EGender;
            else gender = 'other' as EGender;

            const result = await updateMyGender(gender);
            if (result) {
                dispatch(setEmployeeGender(gender));
            }
        } catch (ex) {
            showErrorDialog(ex as Error);
            throw ex;
        }
    };

    const handleSaveLinkedIn = async (value: string) => {
        try {
            const result = await updateMyLinkedInUrl(value || undefined);
            if (result) {
                dispatch(setEmployeeLinkedInUrl(value || undefined));
            }
        } catch (ex) {
            showErrorDialog(ex as Error);
            throw ex;
        }
    };

    const handleSaveSlack = async (value: string) => {
        try {
            const result = await updateMySlackUrl(value || undefined);
            if (result) {
                dispatch(setEmployeeSlackUrl(value || undefined));
            }
        } catch (ex) {
            showErrorDialog(ex as Error);
            throw ex;
        }
    };

    const handleSaveT4loUrl = async (value: string) => {
        try {
            // Extract slug from full URL
            const urlParts = value.split('/p/');
            const slug = urlParts.length > 1 ? urlParts[1] : value;

            if (!validateUrlSlug(slug)) {
                throw new Error(
                    'URL-Slug muss 3-50 Zeichen lang sein und darf nur Buchstaben, Zahlen und Bindestriche enthalten.',
                );
            }

            console.log('Save T4lo URL slug:', slug);
            // TODO: Implement custom URL slug when backend ready
            // const result = await updateEmployeeUrlSlug(slug);
            // if (result) {
            //   dispatch(setEmployeeUrlSlug(slug));
            // }
        } catch (ex) {
            showErrorDialog(ex as Error);
            throw ex;
        }
    };

    return (
        <Box>
            <ProfileTabHeader
                title={langStrings.generalInfo}
                subtitle="Verwalte deine persönlichen Informationen und Einstellungen"
            />

            <Stack spacing={3}>
                {/* Profile Picture Section */}
                <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            Profilbild
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <EmployeePicture avatarSize={80} employee={employee} allowUpload={true} />

                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {employee.firstName} {employee.lastName}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
                                    {OU?.title || 'Keine Organisationseinheit'}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Description */}
                <DescriptionEditor />

                {/* Personal and Company Information */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3 }}>
                    {/* Personal Information */}
                    <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                Persönliche Informationen
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <EditableField
                                    label={langStrings.firstName}
                                    value={employee.firstName}
                                    icon={<Person fontSize="small" />}
                                    onSave={handleSaveFirstName}
                                    disabled={offline}
                                />

                                <EditableField
                                    label={langStrings.lastName}
                                    value={employee.lastName}
                                    icon={<Person fontSize="small" />}
                                    onSave={handleSaveLastName}
                                    disabled={offline}
                                />

                                <EditableField
                                    label="E-Mail"
                                    value={user.email}
                                    icon={<Email fontSize="small" />}
                                    onSave={handleSaveEmail}
                                    disabled={true}
                                />

                                <EditableField
                                    label="Telefonnummer"
                                    value={employee.phoneNumber || ''}
                                    icon={<Email fontSize="small" />}
                                    onSave={async (value) => {
                                        try {
                                            const result = await updateMyPhoneNumber(value || undefined);
                                            if (result) {
                                                dispatch(setEmployeePhoneNumber(value || undefined));
                                            }
                                        } catch (ex) {
                                            showErrorDialog(ex as Error);
                                            throw ex;
                                        }
                                    }}
                                    disabled={offline}
                                />

                                {/* <EditableField
                                    label="Titel/Position"
                                    value={employee.jobTitle || 'Nicht angegeben'}
                                    icon={<Work fontSize="small" />}
                                    onSave={handleSaveTitle}
                                    disabled={offline}
                                /> */}

                                <EditableField
                                    label="T4lo URL"
                                    value={`https://${stageContext.domain}${entityTypeToLink(employee.id, EEntityType.EMPLOYEE)}`}
                                    // value={generatePublicProfileUrl(employee)}
                                    icon={<Link fontSize="small" />}
                                    onSave={handleSaveT4loUrl}
                                    disabled={offline || true}
                                />

                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Person fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            Pronomen
                                        </Typography>
                                    </Box>
                                    <FormControl fullWidth size="small" disabled={offline}>
                                        <Select
                                            value={
                                                employee.gender === 'male'
                                                    ? 'er/ihm'
                                                    : employee.gender === 'female'
                                                      ? 'sie/ihr'
                                                      : employee.gender === 'other'
                                                        ? 'Divers'
                                                        : ''
                                            }
                                            displayEmpty
                                            onChange={(e) => handleSavePronouns(e.target.value)}
                                        >
                                            <MenuItem value="" disabled>
                                                <em>Pronomen auswählen</em>
                                            </MenuItem>
                                            {PRONOUNS.map((pronoun) => (
                                                <MenuItem key={pronoun} value={pronoun}>
                                                    {pronoun}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Home fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            Land/Region
                                        </Typography>
                                    </Box>
                                    <FormControl fullWidth size="small" disabled={offline}>
                                        <Select
                                            value="Deutschland"
                                            displayEmpty
                                            onChange={(e) => handleSaveCountry(e.target.value)}
                                        >
                                            <MenuItem value="" disabled>
                                                <em>Land auswählen</em>
                                            </MenuItem>
                                            {COUNTRIES.map((country) => (
                                                <MenuItem key={country} value={country}>
                                                    {country}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box> */}
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Company Information */}
                    {/* <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Firmen-Informationen
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Business fontSize="small" sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Firma
                    </Typography>
                  </Box>
                  <FormControl fullWidth size="small" disabled={offline}>
                    <Select
                      value={OU?.id || ''}
                      displayEmpty
                      onChange={(e) => handleOUChange(Number(e.target.value))}
                    >
                      <MenuItem value="" disabled>
                        <em>Keine Firma ausgewählt</em>
                      </MenuItem>
                      {OUs?.map((ou) => (
                        <MenuItem key={ou.id} value={ou.id}>
                          {ou.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Business fontSize="small" sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {langStrings.orgUnit}
                    </Typography>
                  </Box>
                  <FormControl fullWidth size="small" disabled={offline}>
                    <Select
                      value={OU?.id || ''}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <em>Nicht zugeordnet</em>
                      </MenuItem>
                      {OUs?.map((ou) => (
                        <MenuItem key={ou.id} value={ou.id}>
                          {ou.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Business fontSize="small" sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Rolle
                    </Typography>
                  </Box>
                  <FormControl fullWidth size="small" disabled={offline}>
                    <Select
                      value={employee.roles?.[0]?.id || ''}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <em>Keine Rolle ausgewählt</em>
                      </MenuItem>
                      {employee.roles?.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <EditableField
                  label="Website der Firma"
                  value="www.ameloot.com"
                  icon={<Language fontSize="small" />}
                  onSave={handleSaveCompanyWebsite}
                  disabled={offline}
                />

                <EditableField
                  label="Firmengröße"
                  value="50-100 Mitarbeiter"
                  icon={<Business fontSize="small" />}
                  onSave={handleSaveCompanySize}
                  disabled={offline}
                />

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Home fontSize="small" sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Standort
                    </Typography>
                  </Box>
                  <FormControl fullWidth size="small" disabled={offline}>
                    <Select
                      value="Berlin"
                      displayEmpty
                      onChange={(e) => handleSaveCountry(e.target.value)}
                    >
                      <MenuItem value="" disabled>
                        <em>Standort auswählen</em>
                      </MenuItem>
                      {COUNTRIES.map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </CardContent>
          </Card>*/}
                </Box>

                {/* Social Media & Links */}
                <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                            Social Media & Verknüpfungen
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                            <EditableField
                                label="LinkedIn"
                                value={employee.linkedInUrl || ''}
                                icon={<LinkedIn fontSize="small" />}
                                onSave={handleSaveLinkedIn}
                                disabled={offline}
                            />

                            {/* <EditableField
                                label="GitHub"
                                value="github.com/maxmustermann"
                                icon={<GitHub fontSize="small" />}
                                onSave={handleSaveGitHub}
                                disabled={offline}
                            />

                            <EditableField
                                label="Twitter/X"
                                value="@maxmustermann"
                                icon={<Twitter fontSize="small" />}
                                onSave={handleSaveTwitter}
                                disabled={offline}
                            /> */}

                            <EditableField
                                label="Slack URL"
                                value={employee.slackUrl || ''}
                                icon={<Share fontSize="small" />}
                                onSave={handleSaveSlack}
                                disabled={offline}
                            />

                            <EditableField
                                label="Website"
                                value={employee.websiteUrl || ''}
                                icon={<Language fontSize="small" />}
                                onSave={async (value) => {
                                    try {
                                        const result = await updateMyWebsiteUrl(value || undefined);
                                        if (result) {
                                            dispatch(setEmployeeWebsiteUrl(value || undefined));
                                        }
                                    } catch (ex) {
                                        showErrorDialog(ex as Error);
                                        throw ex;
                                    }
                                }}
                                disabled={offline}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Stack>
        </Box>
    );
}
